import { Capacitor } from "@capacitor/core";
import type { FirebaseOptions } from "firebase/app";
import type { Auth } from "firebase/auth";
import { STAGE } from "./globals";

export function firebaseConfig(): FirebaseOptions {
  if (STAGE === "development") {
    return {
      apiKey: import.meta.env.VITE_FIREBASE_APIKEY,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      authDomain: "localhost",
    };
  }

  return {
    apiKey: "AIzaSyAuaDop2srxism5qICzN0CDL7Jzr8aCCk8",
    authDomain: "docketauth.firebaseapp.com",
    databaseURL: "https://docketauth.firebaseio.com",
    projectId: "docketauth",
    storageBucket: "docketauth.appspot.com",
    messagingSenderId: "758081979991",
    appId: "1:758081979991:web:fb3518dd7575ab13a1c512",
    measurementId: "G-E801SNV9Z6",
  };
}

let auth: Promise<Auth> | undefined;

/**
 * Firebase Auth, loaded the first time it's needed (only signing in and out use it), so the
 * Firebase SDK stays out of the startup bundle. In development it talks to the local emulator.
 *
 * The apps get Auth without the popup and redirect sign-in, which they don't use (the native plugin
 * signs in). getAuth() includes it, and on iOS its startup waits for an iframe that never answers
 * in the app's web view, so Auth never finishes starting and authStateReady() never resolves.
 */
export function firebaseAuth(): Promise<Auth> {
  auth ??= (async () => {
    const [
      { initializeApp },
      { connectAuthEmulator, getAuth, indexedDBLocalPersistence, initializeAuth },
    ] = await Promise.all([import("firebase/app"), import("firebase/auth")]);
    const app = initializeApp(firebaseConfig());
    const instance = Capacitor.isNativePlatform()
      ? initializeAuth(app, { persistence: indexedDBLocalPersistence })
      : getAuth(app);
    if (STAGE === "development") {
      connectAuthEmulator(instance, "http://127.0.0.1:9099", { disableWarnings: true });
    }
    return instance;
  })();
  return auth;
}

/** Sign out of Firebase, if a Firebase user is signed in (it keeps its own session). */
export async function signOutOfFirebase(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    await signOutOfWebSdk();
    return;
  }
  // The apps sign in to the native Firebase SDK. This signs out of Google too, so the next Google
  // sign-in asks which account again. It's fine with nobody signed in.
  const { FirebaseAuthentication } = await import("@capacitor-firebase/authentication");
  try {
    await FirebaseAuthentication.signOut();
  } finally {
    // The React build also signed in to the web SDK in the apps (an email link opened where it
    // wasn't asked for), and that user and its refresh token stay in the web view's storage until
    // signed out. The apps' Auth has no popup or redirect sign-in, so starting it can't hang.
    await signOutOfWebSdk();
  }
}

/** Sign out of the Firebase JS SDK, if a user is signed in to it. */
async function signOutOfWebSdk(): Promise<void> {
  const instance = await firebaseAuth();
  // The signed-in user is restored from Firebase's own storage after the page loads.
  await instance.authStateReady();
  if (instance.currentUser !== null) {
    const { signOut } = await import("firebase/auth");
    await signOut(instance);
  }
}
